import { useEffect, useState } from 'react';
import { useTheme } from './ThemeProvider';
import { ImagePlaceholder } from './ImagePlaceholder';
import { LANDING_IMAGE_PATHS, resolveLandingImageUrl, type LandingImageSlot } from '../lib/landingAssets';

const aspectClass: Record<'16:9' | '4:3' | '1:1' | '21:9', string> = {
  '16:9': 'aspect-video',
  '4:3': 'aspect-[4/3]',
  '1:1': 'aspect-square',
  '21:9': 'aspect-[21/9]',
};

type Props = {
  slot: LandingImageSlot;
  ratio: '16:9' | '4:3' | '1:1' | '21:9';
  caption: string;
  brief: string;
  className?: string;
  alt?: string;
};

export function LandingIllustration({ slot, ratio, caption, brief, className = '', alt }: Props) {
  const { resolvedScheme } = useTheme();
  const paths = LANDING_IMAGE_PATHS[slot];
  const activeSrc = resolveLandingImageUrl(paths, resolvedScheme);

  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setFailed(false);
    setLoaded(false);
  }, [activeSrc]);

  if (failed) {
    return (
      <div className="land-illus-parallax will-change-transform">
        <div className={`landing-illustration--interactive ${className}`}>
          <ImagePlaceholder ratio={ratio} caption={caption} brief={brief} />
        </div>
      </div>
    );
  }

  return (
    <div className="land-illus-parallax will-change-transform">
      <figure className={`landing-illustration landing-illustration--interactive ${className}`}>
        <div className={`relative w-full overflow-hidden ${aspectClass[ratio]}`}>
          <img
            src={activeSrc}
            alt={alt ?? caption}
            loading="lazy"
            decoding="async"
            className={`absolute inset-0 block h-full w-full object-cover object-center transition-opacity duration-300 ${
              loaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setLoaded(true)}
            onError={() => setFailed(true)}
          />
          {!loaded ? (
            <div className="absolute inset-0 animate-pulse bg-ct-elevated/40" aria-hidden />
          ) : null}
        </div>
      </figure>
    </div>
  );
}
